/**
 * FleetManagementTab · v2.6.7 — Admin Console → Fleet Management
 *
 * Three sub-tabs:
 *   - Fleet Fuel Card  · all submissions with color-coded rows (green/pink-orange/orange/red)
 *   - Vehicle Maintenance · driver vehicles + oil-change status + supervisor override
 *   - Hold List · drivers on auto-hold (filtered view of Vehicle Maintenance)
 */
import { useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Fuel, Wrench, AlertTriangle, ShieldCheck, Download, FileText, RotateCw, Eye, Search, X as XIcon, Pencil } from "lucide-react";
import { fmtUsd } from "@/lib/format";

const TIER_ROW = {
  green: "bg-emerald-500/5 border-l-4 border-l-emerald-400 hover:bg-emerald-500/10",
  pink_orange: "bg-orange-300/10 border-l-4 border-l-pink-400 hover:bg-orange-300/15",
  orange: "bg-orange-500/10 border-l-4 border-l-orange-400 hover:bg-orange-500/15",
  red: "bg-red-500/15 border-l-4 border-l-red-500 hover:bg-red-500/20",
};
const TIER_TEXT = {
  green: "text-emerald-400",
  pink_orange: "text-pink-300",
  orange: "text-orange-300",
  red: "text-red-400",
};

export default function FleetManagementTab() {
  const [sub, setSub] = useState("fuel");
  return (
    <Tabs value={sub} onValueChange={setSub}>
      <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-3 mb-4 h-auto gap-1 p-1" data-testid="fleet-mgmt-subtabs">
        <TabsTrigger value="fuel" data-testid="fm-tab-fuel" className="text-xs sm:text-sm py-2"><Fuel className="h-4 w-4 mr-1"/>Fleet Fuel Card</TabsTrigger>
        <TabsTrigger value="vehicles" data-testid="fm-tab-vehicles" className="text-xs sm:text-sm py-2"><Wrench className="h-4 w-4 mr-1"/>Vehicle Maintenance</TabsTrigger>
        <TabsTrigger value="holds" data-testid="fm-tab-holds" className="text-xs sm:text-sm py-2"><AlertTriangle className="h-4 w-4 mr-1"/>Hold List</TabsTrigger>
      </TabsList>
      <TabsContent value="fuel"><FuelCardAdminPanel/></TabsContent>
      <TabsContent value="vehicles"><VehicleAdminPanel filter="all"/></TabsContent>
      <TabsContent value="holds"><VehicleAdminPanel filter="hold"/></TabsContent>
    </Tabs>
  );
}

// =========================================================================
// Sub-tab 1 — Fleet Fuel Card (admin view of every submission)
// =========================================================================
function FuelCardAdminPanel() {
  const [cards, setCards] = useState([]);
  const [days, setDays] = useState(60);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [openCard, setOpenCard] = useState(null);

  const reload = () => {
    setBusy(true);
    adminApi.get("/admin/fleet/fuel-cards", { params: { days } })
      .then(r => setCards(r.data.items || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load fuel cards"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [days]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return cards;
    return cards.filter(c => (
      (c.driver_name || "").toLowerCase().includes(q)
      || (c.city || "").toLowerCase().includes(q)
      || (c.state || "").toLowerCase().includes(q)
      || (c.place || "").toLowerCase().includes(q)
      || (c.color_tier?.label || "").toLowerCase().includes(q)
    ));
  }, [cards, search]);

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this fuel-card record?")) return;
    try { await adminApi.delete(`/admin/fleet/fuel-cards/${id}`); toast.success("Deleted"); setOpenCard(null); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = () => {
    const url = `${adminApi.defaults.baseURL}/admin/fleet/fuel-cards/export.csv?days=${days}`;
    fetch(url, { headers: adminApi.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `buller-fuel-cards-${days}d.csv`;
        link.click();
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const receiptUrl = (file_id) => file_id ? `${adminApi.defaults.baseURL}/uploads/${file_id}` : null;

  const totals = useMemo(() => ({
    count: filtered.length,
    total_price: filtered.reduce((s, c) => s + (c.price || 0), 0),
    total_gallons: filtered.reduce((s, c) => s + (c.gallons || 0), 0),
    flagged: filtered.filter(c => c.color_tier?.flagged).length,
  }), [filtered]);

  return (
    <div className="space-y-3" data-testid="fuel-admin-panel">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Fuel className="h-5 w-5 text-amber-400"/>Fleet Fuel Card · all submissions</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Each row is color-coded by price + gallons. Red rows are flagged for review. Click a row to see full details, download receipt, or delete.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2 top-2.5 text-zinc-500"/>
              <Input data-testid="fuel-admin-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search driver/city/tier" className="bg-zinc-950 border-zinc-700 text-xs h-8 pl-7 w-56"/>
            </div>
            <Label className="text-xs text-zinc-400">Days</Label>
            <Input data-testid="fuel-admin-days" type="number" min={1} max={365} value={days} onChange={(e)=>setDays(Math.max(1, Math.min(365, parseInt(e.target.value||"60",10))))} className="bg-zinc-950 border-zinc-700 text-zinc-100 h-8 w-20 text-xs"/>
            <Button variant="outline" size="sm" onClick={reload} disabled={busy} className="bg-zinc-950 border-zinc-700 h-8 text-xs"><RotateCw className="h-3 w-3 mr-1"/>Refresh</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} className="bg-zinc-950 border-green-400/40 text-green-300 h-8 text-xs"><Download className="h-3 w-3 mr-1"/>CSV</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
          <Pill label="Submissions" value={totals.count}/>
          <Pill label="Total spent" value={fmtUsd(totals.total_price)} color="text-emerald-400"/>
          <Pill label="Total gallons" value={totals.total_gallons.toFixed(1)}/>
          <Pill label="Flagged" value={totals.flagged} color={totals.flagged > 0 ? "text-red-400" : "text-zinc-300"}/>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 divide-y divide-zinc-900">
        {filtered.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm">No fuel-card submissions match.</div>}
        {filtered.map(c => {
          const tier = c.color_tier?.tier;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenCard(c)}
              className={`w-full text-left p-3 flex items-start justify-between gap-3 transition-colors ${TIER_ROW[tier] || "hover:bg-zinc-900/60"}`}
              data-testid={`fuel-row-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`uppercase text-[10px] ${TIER_TEXT[tier] || ""} border-zinc-700`}>{c.color_tier?.label || "—"}</Badge>
                  {c.color_tier?.flagged && <Badge variant="outline" className="border-red-500/50 text-red-400 text-[10px] uppercase">⚑ Flag</Badge>}
                  <span className="text-[10px] tracking-wider text-zinc-600 font-mono">#{c.id?.slice(0,8).toUpperCase()}</span>
                  <span className="text-xs text-zinc-500">{c.purchase_date}</span>
                </div>
                <div className="text-sm mt-1 truncate">{c.driver_name} · {c.place || "—"}</div>
                <div className="text-[11px] text-zinc-500">{c.city}, {c.state} · {c.odometer} mi{c.notes ? ` · ${c.notes}` : ""}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-bold meter-display ${TIER_TEXT[tier] || "text-amber-400"}`}>{fmtUsd(c.price)}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{c.gallons} gal</div>
                {c.receipt_file_id && <div className="text-[9px] text-emerald-400 mt-0.5">📎 Receipt</div>}
              </div>
            </button>
          );
        })}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!openCard} onOpenChange={(o) => { if (!o) setOpenCard(null); }}>
        <DialogContent className="bg-zinc-950 border-amber-400/30 text-zinc-100 max-w-lg" data-testid="fuel-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2"><Fuel className="h-5 w-5"/>Fuel Card · #{openCard?.id?.slice(0,8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          {openCard && (
            <div className="space-y-3 text-xs">
              <Card className="bg-zinc-900 border-zinc-800 p-3 space-y-0.5">
                <Row k="Driver" v={openCard.driver_name}/>
                <Row k="Submitted by" v={openCard.submitted_by_name}/>
                <Row k="Date" v={openCard.purchase_date}/>
                <Row k="Place" v={openCard.place}/>
                <Row k="Location" v={`${openCard.city}, ${openCard.state}`}/>
                <Row k="Odometer" v={`${openCard.odometer} mi`}/>
                <Row k="Notes" v={openCard.notes || "—"}/>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800 p-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Price</div><div className={`text-xl font-bold meter-display ${TIER_TEXT[openCard.color_tier?.tier] || "text-amber-400"}`}>{fmtUsd(openCard.price)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Gallons</div><div className="text-xl font-bold meter-display text-zinc-200">{openCard.gallons}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Tier</div><div className={`text-sm font-bold uppercase ${TIER_TEXT[openCard.color_tier?.tier] || "text-zinc-300"}`}>{openCard.color_tier?.label || "—"}</div></div>
                </div>
              </Card>
              {openCard.receipt_file_id && (
                <Card className="bg-zinc-900 border-zinc-800 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-2">Receipt</div>
                  <img
                    src={receiptUrl(openCard.receipt_file_id)}
                    alt="receipt"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="max-h-72 rounded border border-zinc-800 mx-auto"
                  />
                  <div className="flex justify-end mt-2">
                    <a href={receiptUrl(openCard.receipt_file_id)} target="_blank" rel="noopener noreferrer" download className="text-xs text-amber-400 hover:text-amber-300" data-testid="fuel-receipt-download">↓ Download original</a>
                  </div>
                </Card>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenCard(null)} className="bg-zinc-950 border-zinc-700 text-zinc-300">Close</Button>
            <Button variant="outline" onClick={() => remove(openCard?.id)} className="bg-red-500/10 border-red-400/40 text-red-300 hover:bg-red-500/20" data-testid="fuel-detail-delete">
              <XIcon className="h-3 w-3 mr-1"/>Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// Sub-tab 2 + 3 — Vehicle Maintenance / Hold List
// =========================================================================
function VehicleAdminPanel({ filter }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openOverride, setOpenOverride] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ pin: "", reason: "promise_to_correct", notes: "" });
  // v2.6.7 batch I · admin can now EDIT a driver's vehicle info + odometer.
  // Drivers can still self-edit via /fleet/vehicle/me — this is for cases
  // where the dispatcher updates on the driver's behalf.
  const [editVeh, setEditVeh] = useState(null);
  const [vehForm, setVehForm] = useState({ make: "", model: "", year: "", color: "", plate_number: "", vin: "", current_odometer: "" });

  const reload = () => {
    setBusy(true);
    adminApi.get("/admin/fleet/vehicles")
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (filter === "hold") return items.filter(d => d.maintenance?.status === "hold" && !d.oil_hold_overridden);
    return items;
  }, [items, filter]);

  const submitOverride = async () => {
    if (!openOverride) return;
    if (!overrideForm.pin) { toast.error("Your supervisor PIN is required"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/fleet/vehicles/${openOverride.id}/override-hold`, {
        pin: overrideForm.pin.trim(),
        driver_id: openOverride.id,
        reason: overrideForm.reason,
        notes: overrideForm.notes,
      });
      toast.success(`Hold released for ${openOverride.name}`);
      setOpenOverride(null);
      setOverrideForm({ pin: "", reason: "promise_to_correct", notes: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally { setBusy(false); }
  };

  const statusBadge = (m, holdActive, overridden) => {
    if (overridden) return <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[10px] uppercase">Override Active</Badge>;
    if (holdActive) return <Badge variant="outline" className="border-red-500/60 text-red-400 text-[10px] uppercase">⚠ ON HOLD</Badge>;
    if (m?.status === "warning") return <Badge variant="outline" className="border-orange-400/40 text-orange-300 text-[10px] uppercase">▲ Due Soon</Badge>;
    if (m?.status === "ok") return <Badge variant="outline" className="border-emerald-400/40 text-emerald-400 text-[10px] uppercase">OK</Badge>;
    return <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px] uppercase">No data</Badge>;
  };

  return (
    <div className="space-y-3" data-testid={`vehicle-admin-${filter}`}>
      <Card className="bg-zinc-900 border-zinc-800 p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            {filter === "hold" ? <AlertTriangle className="h-5 w-5 text-red-400"/> : <Wrench className="h-5 w-5 text-amber-400"/>}
            {filter === "hold" ? "Drivers on Hold" : "Vehicle Maintenance"}
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {filter === "hold"
              ? "Drivers currently auto-held because their oil change is >1,000 miles overdue. Use the supervisor override to release them with a 'Promise to Correct'."
              : "All drivers with vehicle info on file. Orange triangle = due soon, red badge = on hold."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={busy} className="bg-zinc-950 border-zinc-700 h-8 text-xs"><RotateCw className="h-3 w-3 mr-1"/>Refresh</Button>
      </Card>

      {filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500 text-sm">
          {filter === "hold" ? "No drivers on hold — all clear." : "No drivers / vehicles to display."}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <Card key={d.id} className={`p-3 ${d.hold_active ? "bg-red-500/5 border-red-500/40" : "bg-zinc-900 border-zinc-800"}`} data-testid={`veh-row-${d.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{d.name}</span>
                    <Badge variant="outline" className="text-[10px] uppercase border-zinc-700">{d.role}</Badge>
                    {statusBadge(d.maintenance, d.hold_active, d.oil_hold_overridden)}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">{[d.vehicle_year, d.vehicle_color, d.vehicle_make, d.vehicle_model].filter(Boolean).join(" ") || "No vehicle info"}{d.plate_number ? ` · ${d.plate_number}` : ""}</div>
                  <div className="text-[10px] text-zinc-500">Odometer: {d.current_odometer || "—"} mi</div>
                </div>
                <div className="md:col-span-1 text-xs">
                  {d.maintenance?.last_oil_change?.service_date ? (
                    <>
                      <div className="text-zinc-400">Last: <span className="text-zinc-200">{d.maintenance.last_oil_change.service_date} · {d.maintenance.last_oil_change.odometer?.toFixed?.(0)} mi</span></div>
                      <div className="text-zinc-400">Type: <span className="text-zinc-200 uppercase text-[10px]">{(d.maintenance.last_oil_change.oil_type || "").replace(/_/g," ")}</span></div>
                      <div className="text-zinc-400">Next due: <span className={d.hold_active ? "text-red-400 font-bold" : "text-amber-300"}>{d.maintenance.last_oil_change.next_due_odometer?.toFixed?.(0)} mi</span></div>
                    </>
                  ) : <div className="text-zinc-600">No oil change history</div>}
                </div>
                <div className="flex flex-col gap-2 justify-end">
                  <Button data-testid={`veh-edit-${d.id}`} size="sm" variant="outline" onClick={() => {
                    setEditVeh(d);
                    setVehForm({
                      make: d.vehicle_make || "",
                      model: d.vehicle_model || "",
                      year: d.vehicle_year || "",
                      color: d.vehicle_color || "",
                      plate_number: d.plate_number || "",
                      vin: d.vin || "",
                      current_odometer: d.current_odometer ?? "",
                    });
                  }} className="bg-zinc-950 border-zinc-700 hover:border-amber-400/60 text-xs h-7">
                    <Pencil className="h-3 w-3 mr-1"/>Edit
                  </Button>
                  {d.hold_active && (
                    <Button data-testid={`veh-override-${d.id}`} size="sm" onClick={() => setOpenOverride(d)} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs">
                      <ShieldCheck className="h-3 w-3 mr-1"/>Override Hold
                    </Button>
                  )}
                  {d.oil_hold_overridden && (
                    <div className="text-[10px] text-amber-300">⚠ Override until {d.oil_hold_override_until_odometer?.toFixed?.(0)} mi · by {d.oil_hold_overridden_by}</div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit vehicle dialog */}
      <Dialog open={!!editVeh} onOpenChange={(o) => { if (!o) setEditVeh(null); }}>
        <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100 max-w-lg" data-testid="veh-edit-dialog">
          <DialogHeader><DialogTitle className="text-amber-400">Edit vehicle · {editVeh?.name}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-400 -mt-1">All fields are optional. Changes are picked up by the oil-change hold engine + fuel-card policies immediately.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Year</Label>
              <Input data-testid="veh-edit-year" type="number" value={vehForm.year} onChange={e => setVehForm(f => ({...f, year: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Color</Label>
              <Input data-testid="veh-edit-color" value={vehForm.color} onChange={e => setVehForm(f => ({...f, color: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Make</Label>
              <Input data-testid="veh-edit-make" value={vehForm.make} onChange={e => setVehForm(f => ({...f, make: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Model</Label>
              <Input data-testid="veh-edit-model" value={vehForm.model} onChange={e => setVehForm(f => ({...f, model: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Plate #</Label>
              <Input data-testid="veh-edit-plate" value={vehForm.plate_number} onChange={e => setVehForm(f => ({...f, plate_number: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">VIN</Label>
              <Input data-testid="veh-edit-vin" value={vehForm.vin} onChange={e => setVehForm(f => ({...f, vin: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1"/>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-zinc-500">Current Odometer (mi)</Label>
              <Input data-testid="veh-edit-odometer" type="number" step="1" value={vehForm.current_odometer} onChange={e => setVehForm(f => ({...f, current_odometer: e.target.value}))} className="bg-zinc-950 border-zinc-700 h-9 mt-1 meter-display"/>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditVeh(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button data-testid="veh-edit-save" onClick={async () => {
              setBusy(true);
              try {
                const payload = {
                  make: vehForm.make || null,
                  model: vehForm.model || null,
                  year: vehForm.year ? parseInt(vehForm.year, 10) : null,
                  color: vehForm.color || null,
                  plate_number: vehForm.plate_number || null,
                  vin: vehForm.vin || null,
                  current_odometer: vehForm.current_odometer !== "" ? parseFloat(vehForm.current_odometer) : null,
                };
                await adminApi.put(`/admin/fleet/vehicles/${editVeh.id}`, payload);
                toast.success(`Vehicle for ${editVeh.name} updated`);
                setEditVeh(null);
                reload();
              } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
              finally { setBusy(false); }
            }} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Save Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={!!openOverride} onOpenChange={(o) => { if (!o) setOpenOverride(null); }}>
        <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100" data-testid="veh-override-dialog">
          <DialogHeader><DialogTitle className="text-amber-400">Override hold · {openOverride?.name}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-400 -mt-1">Releases the driver's auto-hold so they can keep working. Logged to audit trail. Hold re-engages once the driver crosses the extended mileage tolerance.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Reason</Label>
              <Select value={overrideForm.reason} onValueChange={(v) => setOverrideForm({...overrideForm, reason: v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="veh-override-reason"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                  <SelectItem value="promise_to_correct">Promise to correct (driver scheduling service)</SelectItem>
                  <SelectItem value="vehicle_serviced">Vehicle serviced but record not logged yet</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Notes (optional)</Label>
              <Input data-testid="veh-override-notes" value={overrideForm.notes} onChange={(e)=>setOverrideForm({...overrideForm, notes: e.target.value})} placeholder="e.g. service scheduled for Tuesday" className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Your supervisor PIN</Label>
              <Input data-testid="veh-override-pin" type="password" inputMode="numeric" value={overrideForm.pin} onChange={(e)=>setOverrideForm({...overrideForm, pin: e.target.value.replace(/\D/g, "").slice(0, 8)})} placeholder="• • • • • •" className="bg-zinc-950 border-zinc-700 text-zinc-100 font-mono tracking-[0.4em] text-center"/>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenOverride(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button data-testid="veh-override-submit" onClick={submitOverride} disabled={busy || !overrideForm.pin} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Override &amp; Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Pill({ label, value, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold ${color || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-0.5">
      <div className="text-zinc-500 uppercase tracking-wider text-[10px] self-center">{k}</div>
      <div className="text-zinc-200 text-right break-words">{v}</div>
    </div>
  );
}
